import React, { useEffect, useState } from "react";
import {
  faCloudArrowUp,
  faTimes,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { API } from "../../utils/Apis";
import { showToast } from "../../utils/helpers";

import CustomButton from "../../Components/Button";

interface mediaTypes {
  id: string;
  url: string;
  number: number;
  type: string;
}

interface SpaceInfoStepFiveType {
  spaceId: string;
  media: Array<mediaTypes> | undefined;
  updateMedia: Function;
  dispatchItem?: Function;
  updateLastSteps?: Function;
  isOnBoarding?: boolean;
  onBack?: (() => void) | null;
  // When false, hide the Save button (e.g. an admin viewing a space they don't
  // own — the editor is read-only).
  canSave?: boolean;
  // When provided, this is the final step of the Add Space flow: the Images
  // action becomes a clearly-labelled "Finish" button that saves and then runs
  // onFinish (e.g. close the modal with a completion message).
  onFinish?: (() => void) | null;
  finishLabel?: string;
}
interface fileTypes {
  file: any;
  url: any;
  type: string;
}

const SpaceInfoStepFive: React.FC<SpaceInfoStepFiveType> = ({
  media,
  spaceId,
  updateMedia,
  dispatchItem,
  updateLastSteps,
  isOnBoarding = false,
  onBack = null,
  canSave = true,
  onFinish = null,
  finishLabel = "Finish",
}) => {
  const [loading, setLoading] = useState(false);
  const [finishBtn, setFinishBtn] = useState(false);
  const [newImages, setNewImages] = useState<Array<fileTypes>>([]);
  const [spaceImages, setImages] = useState<Array<mediaTypes> | undefined>([]);
  const [mediaError, setMediaError] = useState("");

  const [mediaOperations, setMediaOperations] = useState<{
    delete: Array<string>;
    reorder: Array<string>;
  }>({
    delete: [],
    reorder: [],
  });

  const selectMedia = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || [];
    if (files.length > 0) {
      setMediaError("");
    }
    const tempImgs = [...newImages];

    const currentImages = [...newImages, ...(spaceImages || [])].filter(
      (item) => item.type === "image"
    );
    const currentVideos = [...newImages, ...(spaceImages || [])].filter(
      (item) => item.type === "video"
    );

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileType = String(file["type"]).includes("image/")
        ? "image"
        : "video";

      const imageCount =
        currentImages.length +
        tempImgs.filter((img) => img.type === "image").length;
      const videoCount =
        currentVideos.length +
        tempImgs.filter((img) => img.type === "video").length;

      if (fileType === "image" && imageCount >= 7) {
        showToast("error", "Maximum 7 images allowed");
        break;
      }

      if (fileType === "video" && videoCount >= 3) {
        showToast("error", "Maximum 3 videos allowed");
        break;
      }

      console.log("TempFile :", file);

      tempImgs.push({
        file: file,
        url: URL.createObjectURL(file),
        type: fileType,
      });
    }

    setNewImages(tempImgs);
  };

  const deleteNewImg = (deleteIndex: number) => {
    // console.log("Delete :", deleteIndex);

    const tempImgs = newImages.filter((item, index) => index !== deleteIndex);

    setNewImages(tempImgs);
  };

  const deleteCurrentImg = (item: mediaTypes, delIndex: number) => {
    const tempDelete = [...mediaOperations?.delete];

    tempDelete.push(item.url);

    setMediaOperations({
      ...mediaOperations,
      delete: tempDelete,
    });

    const tempImgs = spaceImages
      ? spaceImages.filter((item, index) => index !== delIndex)
      : [];

    setImages(tempImgs);
  };

  const uploadImages = (createNew = false, finish = false) => {
    let errs = false;

    // At least one image is required. Count existing images that aren't pending
    // deletion plus any newly added images.
    const remainingExistingImages = (spaceImages || []).filter(
      (item) =>
        item.type === "image" &&
        !(mediaOperations.delete || []).includes(item.id)
    ).length;
    const newImageCount = newImages.filter(
      (img) => img.type === "image"
    ).length;

    if (remainingExistingImages + newImageCount === 0) {
      setMediaError("Please add at least one image of the space.");
      showToast("error", "Please add at least one image of the space.");
      errs = true;
    } else {
      setMediaError("");
    }

    // Validate file type limits
    const imageCount = newImages.filter((img) => img.type === "image").length;
    const videoCount = newImages.filter((img) => img.type === "video").length;

    if (imageCount > 7) {
      showToast("error", "Maximum 7 images allowed");
      errs = true;
    }

    if (videoCount > 3) {
      showToast("error", "Maximum 3 videos allowed");
      errs = true;
    }

    if (!finishBtn && !loading && !errs) {
      if (createNew) {
        setLoading(true);
      } else {
        setFinishBtn(true);
      }

      const formData = new FormData();

      if (newImages.length > 0) {
        for (let i = 0; i < newImages.length; i++) {
          formData.append("files", newImages[i].file);
        }
      }

      // Add Delete Items
      if (mediaOperations.delete && mediaOperations.delete.length > 0) {
        const deleteItems = mediaOperations.delete || [];
        for (let i = 0; i < deleteItems.length; i++) {
          formData.append("mediaOperations[delete][]", deleteItems[i]);
        }
      }

      // Add Reorder Items
      if (mediaOperations.reorder && mediaOperations.reorder.length > 0) {
        const reorderItems = mediaOperations.reorder || [];
        for (let i = 0; i < reorderItems.length; i++) {
          formData.append("mediaOperations[reorder][]", reorderItems[i]);
        }
      }

      formData.append("id", spaceId);

      API.updateSpaceImages(formData)
        .then((res) => {
          const type = res.type === "success" ? "success" : "error";
          showToast(type, res.message);

          if (res.type === "success") {
            setNewImages([]);

            if (res && res.data && res.data.media) {
              const medias = res.data.media;

              if (isOnBoarding) {
                updateLastSteps(createNew);
              } else {
                updateMedia(medias);
              }
            }

            if (dispatchItem) {
              dispatchItem(res.data);
            }

            // Final step of the Add Space flow → run the completion handler
            // (e.g. close the modal with a "space set up" message).
            if (finish && onFinish) {
              onFinish();
            }
          }
        })
        .catch((err) => {
          showToast("error", err.message);
        })
        .finally(() => {
          if (createNew) {
            setLoading(false);
          } else {
            setFinishBtn(false);
          }
        });
    }
  };

  useEffect(() => {
    console.log("mediaOperations :", mediaOperations);
  }, [mediaOperations]);

  useEffect(() => {
    console.log("updatedImage :", media);

    if (media?.length) {
      setImages(media);
    }
  }, [media]);

  return (
    <div className="">
      <div className="form-group mb-2">
        <label>
          Images<span className="required-star">*</span>
        </label>
        <p className="required-note m-0">
          At least one image is required.
        </p>
      </div>

      <div
        className={`upload-field-wrapper${mediaError ? " has-error" : ""}`}
      >
        <input
          type="file"
          multiple
          className=""
          onChange={selectMedia}
          accept="image/*,video/*"
        />
        <span className="upload-icon">
          <FontAwesomeIcon icon={faCloudArrowUp} />
        </span>
        <p>
          Images: PNG, JPG - Max 7 images (auto-optimized to fit 1200x900px)
        </p>
        <p>Videos: MP4, AVI, MOV, WMV, FLV, WEBM - Max 3 videos</p>
        <p>Maximum 10 media files total (7 images + 3 videos)</p>
        <h4>
          <span>Click to upload</span> or drag and drop
        </h4>
      </div>

      <div className="uploaded-imgs-list">
        <div className="row">
          {spaceImages && (
            <>
              {spaceImages.map((item, index) => (
                <div
                  className="col-xl-2 col-md-3 col-sm-3 col-xs-12"
                  key={`existing-${index}`}
                >
                  <div className="uploaded-img-item">
                    {item.type === "image" && <img src={item.url} alt="" />}

                    {item.type === "video" && (
                      <div className="video-preview">
                        <FontAwesomeIcon icon={faVideo} color="#000" />
                      </div>
                    )}

                    <span
                      className="remove-icon"
                      onClick={() => deleteCurrentImg(item, index)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}

          {newImages && (
            <>
              {newImages.map((item, index) => (
                <div
                  className="col-xl-2 col-md-3 col-sm-3 col-xs-12"
                  key={`new-images-${index}`}
                >
                  <div className="uploaded-img-item">
                    {item.type === "image" && <img src={item.url} alt="" />}

                    {item.type === "video" && (
                      <div className="video-preview">
                        <FontAwesomeIcon icon={faVideo} color="#000" />

                        <video
                          src={item.url}
                          style={{ width: "100%", height: "100px" }}
                        />
                      </div>
                    )}

                    <span
                      className="remove-icon"
                      onClick={() => deleteNewImg(index)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div
        className={`navigation-btns mt-4 ${
          onBack ? "justify-content-between" : "justify-content-end"
        }`}
      >
        {onBack && (
          <div className="nav-btn-group nav-btn-group-back">
            <CustomButton
              classList="btn-secondary"
              title="Back"
              onClick={() => onBack()}
            />
          </div>
        )}

        <div className="nav-btn-group">
          {!isOnBoarding && canSave && !onFinish && (
            <CustomButton
              isLoading={loading}
              title="Save"
              onClick={() => uploadImages()}
            />
          )}

          {!isOnBoarding && canSave && onFinish && (
            <CustomButton
              isLoading={loading}
              title={finishLabel}
              onClick={() => uploadImages(false, true)}
            />
          )}

          {isOnBoarding && (
            <>
              <CustomButton
                isLoading={loading}
                title="Save & Add New"
                onClick={() => uploadImages(true)}
              />

              <CustomButton
                isLoading={finishBtn}
                title="Finish"
                onClick={() => uploadImages()}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpaceInfoStepFive;
